import {useState, useEffect, useRef} from 'react';
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highmaps";
import "highcharts/modules/accessibility";

import { useErrorBoundary } from 'react-error-boundary';
import {Box, debounce, FormControl, InputAdornment, Stack, TextField, Typography} from '@mui/material/';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import Grid from '@mui/material/Grid2';
import TownInfo from '../../../../types/municipal/townInfo';

interface MapDataType {
    objects: { TOWNSSURVEY_POLY: { geometries: any[] } }
}

interface TownDropdownOption {
    code: number;
    name: string;
    county: string;
}

const MunicipalMap = (): JSX.Element => {

    const { showBoundary } = useErrorBoundary();  

    const [townMapData, setTownMapData] = useState<MapDataType | null>(null);
    const [townInfoData, setTownInfoData] = useState<any[]>([]);
   
    const [townListOpen, setTownListOpen] = useState(false);
    const [townOptions, setTownOptions] = useState<readonly TownDropdownOption[]>([]);
    const [selectedTown, setSelectedTown] = useState<TownDropdownOption | undefined>(undefined);

    const mapRef = useRef<HighchartsReact.RefObject>(null);

    const handleTownListOpen = () => {
      setTownListOpen(true);
    };
  
    const handleTownListClose = () => {
      setTownListOpen(false);
      setTownOptions([]);
    };

    useEffect( () => {
        getCitiesData();
      }, []);

    const getMapData = async (cities: TownInfo[]) => {

      const url = `${process.env.PUBLIC_URL}/ma/us-ma-town.topo.txt`;

      try{
            await fetch(url)
            .then(result => {
              if (!result.ok) {
                //showBoundary(new Error('Failed to get the MAP data.'));
                //do not show map if data is not available
                setTownMapData(null);
              }
              return result.json();
            })
            .then(result => {
                 
              let data = result as MapDataType;
            
              let townInLandData = data.objects.TOWNSSURVEY_POLY.geometries.filter((d: { properties: { COASTAL_PO: string; }; }) => d.properties.COASTAL_PO == "NO");
              data.objects.TOWNSSURVEY_POLY.geometries = townInLandData;

              townInLandData.forEach((town: any) => {
                let city = cities?.find((city: TownInfo) => { return city.code == town.properties.TOWN_ID });
                town["value"] = city?.pop2020;
                town["id"] = city?.code;
                let filers = city?.electedFilers;
                let filersHtml = '';
                filers?.forEach((filer: any) => {
                    if (filer.isSenate) {
                        filersHtml += `<tr><td><b>${filer.filerName}</b></td><td><i>Senator</i></td><td><i>${filer.districtNameHeld}</i></td></tr>`;
                    }
                    if (filer.isHouse) {
                        filersHtml += `<tr><td><b>${filer.filerName}</b></td><td><i>Representative</i></td><td><i>${filer.districtNameHeld}</i></td></tr>`;
                    }
                })
                town["filersHtml"] = filersHtml;
            });
              setTownMapData(data);
              setTownInfoData(townInLandData);
            });
          }catch(error){
              console.error('Uncaught Error:', error);
          }
    }

    const getCitiesData = async () => {
        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/municipalities`)
            .then(result => {
              if (!result.ok) {
                showBoundary(new Error('Failed to get the towns data.'));
              }
              return result.json();
            })
            .then(result => {
                getMapData(result);
            });
          }catch(error){
              console.error('Uncaught Error:', error);
          }
    }

    const mapOptions = {

        chart: {
            map: townMapData,
            height: 600
        },
        title: {
            text: ""
        },
        mapNavigation: {
            enabled: true,
            buttonOptions: {
                verticalAlign: 'bottom'
            }
        },
        legend: {
            title: {
                text: 'Town/City Population (2020)'
            },
            align: 'center',
            verticalAlign: 'bottom',
            floating: true,
            layout: 'horizontal',
            valueDecimals: 0,
            backgroundColor: 'rgba(255,255,255,0.9)',
        },
        colorAxis: {
            min: 1000,
            minColor: '#6EAFD5',
            maxColor: '#083F80',
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
        tooltip: {
            pointFormat: `<span class="has-text-blue"><b>{point.properties.TOWN}</b></span>
                          <br><span>Population (2020): {point.value}</span>
                          <br><br><span><b>Elected Officials:</b></span><br><table class="table">{point.filersHtml}</table>`,
            shared: true,
            useHTML: true,
        },
        plotOptions: {
            series: {
                point: {
                    events: {
                        click: function (this: Highcharts.Point) {
                            window.location.href = `/municipal/town?cityCode=${this.properties.TOWN_ID}`;
                        },
                        select: function (this: Highcharts.Point, e: any) {
                            let chart = this.series.chart;
                            let selectedPoints = chart.getSelectedPoints();
                            let cityCode = this.properties.TOWN_ID;
                            let point = selectedPoints.find((p) => { return p.options.id == cityCode });
                            //var point = chart.series[0].data[chart.series[0].data.length - 1];
                            if (point) {
                                chart.tooltip.refresh([point]);
                            }
                            //this.series.chart.refresh(chart.series[0].data[chart.series[0].data.length - 1]);
                        }
                    }
                }
            }
        },
        series: [{
            data: townInfoData,
            joinBy: null,
            allowPointSelect: true,
            name: 'Town/City',
            states: {
                hover: {
                    color: '#a4edba'
                },
                select: {
                    color: '#a4edba',
                    borderColor: 'black',
                }
            },
            dataLabels: {
                enabled: false,
                format: '{point.Name}'
            }
        }]
    }


    const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        (async () => {
          refreshTownList(searchInput);
        })();
      }, 500);
    
    
      const updateSelectedTown = (town: TownDropdownOption) => {
        let cityCode = town.code;
        if (mapRef.current) {
            const chart = mapRef.current.chart;
            const point = mapRef.current?.chart.get(cityCode.toString());
            if (point) {
                (point as Highcharts.Point).select(true, true);
            }
            //point?.select(true, true);
        }
        setSelectedTown(town);
        window.location.href = `/municipal/town?cityCode=${cityCode}`;
      }
    
      const clearSelectedTown = () => {
        setSelectedTown(undefined);
      }
    
    
      const refreshTownList = async (searchInput: string) => {
        try{
          if(searchInput){
            let encodedSearchInput = encodeURIComponent(searchInput);
            let url = `${process.env.REACT_APP_API_BASE_URL}/municipalityList?searchPhrase=${encodedSearchInput}`;
            await fetch(url)
            .then(result => result.json())
            .then(result => {
                setTownOptions(result);
            });
          }
          else{
            setTownOptions([]);
          }
    
        }catch(error){
          console.error('Uncaught Error:', error);
        }
      }
    

    return (
    <Box marginBottom={4}>
      <Box marginBottom={4}>
        <Typography
          variant="h4"
          data-aos={'fade-up'}
          align={'center'}
          fontWeight={700}
          gutterBottom
        >
          Massachusetts Towns & Cities
        </Typography>
        <Typography
          variant="h6"
          color={'text.secondary'}
          align={'center'}
          data-aos={'fade-up'}>
          Learn more about elected officials and races in your city or town
        </Typography>
      </Box>
      <Grid container spacing={4}>
        <Grid size={12} 
            display={"flex"} 
            justifyContent={"center"}
            data-aos={'fade-up'}
        >
            <FormControl variant='standard'>
                  <Autocomplete
                    filterOptions={(x) => x}
                    sx={{ width: 320 }}
                    onChange={(event: any, newValue: TownDropdownOption | null) => {
                      if (newValue) {
                          updateSelectedTown(newValue);
                      }
                      else{
                          clearSelectedTown();
                      }
                    }}
                    open={townListOpen}
                    onOpen={handleTownListOpen}
                    onClose={handleTownListClose}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => 
                      (
                        <Stack 
                          direction="row" 
                          display={"flex"} 
                          justifyContent={"flex-start"}
                          key={option.code}
                          marginLeft={1}
                        >
                            <AccountBalanceIcon />
                            <Typography variant='body2' {...props}>
                                {option.name}
                            </Typography>
                        </Stack>
                        )
                    }
                    options={townOptions}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='standard'
                        placeholder={"Search your city or town..."}
                        onChange={filterByName}
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
            </FormControl>
        </Grid>
        <Grid size={12} marginTop={2}>
          {
            townMapData && townInfoData.length > 0 &&
           <HighchartsReact
                ref={mapRef}
                highcharts={Highcharts}
                options={mapOptions}
                constructorType={'mapChart'}
            />
          }
        </Grid>
      </Grid>
    </Box>
  );
};

export default MunicipalMap;

